import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { Box, Button, Flex, Stack, Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ManifestCheckbox } from '../../components/ManifestCheckbox/ManifestCheckbox';
import { ManifestNotifications } from '../../hooks/utils/useNotifications';
import { PutFeatureFlagPayload } from './api/administration.api';
import { usePutFeatureFlag } from './hooks/query/usePutFeatureFlag';

export const FeatureFlags = () => {
  const { t } = useTranslation();
  const {
    mutate: putFeatureFlag,
    isLoading,
    isError,
    error,
    isSuccess,
  } = usePutFeatureFlag();

  const form = useForm<PutFeatureFlagPayload>({
    initialValues: {
      name: '',
      description: '',
      enabledForOrganizationIds: [],
      isEnabledForWholePlatform: true,
    },
    validate: (values: PutFeatureFlagPayload) => {
      const errors: Partial<Record<keyof PutFeatureFlagPayload, string>> = {};
      if (values.name.length === 0) {
        errors.name = t('global.form.errors.requiredFieldWithName', { field: 'Name' });
      }

      if (values.description.length === 0) {
        errors.description = t('global.form.errors.requiredFieldWithName', {
          field: 'Description',
        });
      }

      return errors;
    },
  });

  useEffect(() => {
    if (isSuccess) {
      form.reset();

      ManifestNotifications.showSuccessNotification({
        title: t('global.success'),
        message: t('page.administrationFeatureFlags.successDescription'),
      });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      ManifestNotifications.showErrorNotification({
        title: t('global.unexpectedError'),
        message: t('global.unexpectedErrorSubtitle'),
      });

      Sentry.captureMessage(`Error creating feature flag: ${error}`, 'error');
      Sentry.captureException(error);
    }
  }, [isError, error]);

  return (
    <div>
      <Flex justify="center">
        <Box w="500px" mt="24px">
          <Stack mb="16px">
            <TextComponent variant="headerMedium">
              {t('page.administrationFeatureFlags.title')}
            </TextComponent>
            <TextComponent variant="bodyMedium">
              {t('page.administrationFeatureFlags.subtitle')}
            </TextComponent>
          </Stack>
          <form
            onSubmit={form.onSubmit(async (values: PutFeatureFlagPayload) => {
              await putFeatureFlag(values);
            })}
          >
            <Stack>
              <TextInput
                label={t('page.administrationFeatureFlags.name')}
                description={t('page.administrationFeatureFlags.nameDescription')}
                required
                {...form.getInputProps('name')}
              />
              <Textarea
                label={t('page.administrationFeatureFlags.description')}
                required
                {...form.getInputProps('description')}
              />

              <Textarea
                label={t('page.administrationFeatureFlags.enabledForOrganizationIds')}
                description={t(
                  'page.administrationFeatureFlags.enabledForOrganizationIdsLabelDescription',
                )}
                {...form.getInputProps('enabledForOrganizationIds')}
                onChange={(e) => {
                  form.setFieldValue(
                    'enabledForOrganizationIds',
                    e.target.value.split(','),
                  );
                }}
              />
              <ManifestCheckbox
                checked={form.values.isEnabledForWholePlatform}
                label={t('page.administrationFeatureFlags.enabledForWholePlatform')}
                {...form.getInputProps('isEnabledForWholePlatform')}
              />

              <Flex justify="flex-end" mt="16px">
                <Button type="submit" loading={isLoading}>
                  {t('page.administrationFeatureFlags.submit')}
                </Button>
              </Flex>
            </Stack>
          </form>
        </Box>
      </Flex>
    </div>
  );
};
